/* eslint-disable react-hooks/exhaustive-deps */
import {Col, Modal, Row} from "antd"
import {BaseButton} from "components"
import {KlassDropdown} from "components/Select"
import {formatCurrency, handleError, toastSuccess} from "helpers"
import {KlassappTableHOC} from "HOC"
import {useModel, useStudentPaymentMethods, useVisible} from "hooks"
import {isEmpty, uniqBy} from "lodash"
import React, {useCallback, useEffect, useMemo, useState} from "react"
import {useTranslation} from "react-i18next"
import {studentPaymentService} from "services"
import {KlassappTableProps} from "types/common"
import {FilterKey} from "types/filter"
import {BaseNewFilter, KlassappTable, KlassappTableHeader} from "uiKit"
import RecurringPaymentPopup from "./RecurringPaymentPopup"
import styles from "./RecurringPayments.module.css"
import {
    PaymentProvider,
    RecurringPaymentStatus,
    StudentPaymentMethod,
    StudentRecurringPayment,
    StudentRecurringPaymentPlanItem
} from "types/student-account/student-payment"
import {PaymentPlanItemStatus} from "types/student-account/payment-plan"
import moment from "moment"

const INITIAL_FILTER = {paymentPlans: []}

type Props = KlassappTableProps & {
    profileId: number
}

const RecurringPayments: React.FC<Props> = (props: Props) => {
    const {
        dispatch,
        dispatchFunc,
        page,
        total,
        pageSize,
        columns,
        data,
        allFields,
        fields,
        tableHeaderActions,
        isLoading,
        menuActions,
        isHideMenuActions,
        isShowTableHeaderAction,
        isLoadedTableFuncs,
        onClickShowConfirmModal,
        getCurrentData,
        onChangePage,
        onChangeRowPerPage,
        onChangeFields,
        onDraggableColumn,
        onChangeAllFields,
        onUpdateRowData,
        onUpdateTableData,
        profileId
    } = props
    const model = useModel()
    const userDateFormat = model.getUserDateFormat()
    const filterKey = FilterKey.StudentAccountRecurringPayments
    const storageData = model.getStorageFilter(filterKey)
    const {t} = useTranslation(["payment", "common"])

    //#region filter

    const [search, setSearch] = useState(!isEmpty(storageData) ? storageData.search : "")
    const [filter, setFilter] = useState(!isEmpty(storageData?.filter) ? storageData.filter : INITIAL_FILTER)
    const [appliedFilter, setAppliedFilter] = useState(filter)
    const [allPayments, setAllPayments] = useState<StudentRecurringPayment[]>([])

    const onChangeFilter = useCallback((key: keyof typeof filter, value: any) => {
        setFilter((prev) => ({...prev, [key]: value}))
    }, [])

    const onClearFilter = useCallback(() => {
        setFilter(INITIAL_FILTER)
        setAppliedFilter(INITIAL_FILTER)
    }, [])

    const onApplyFilter = useCallback(() => {
        setAppliedFilter(filter)
        model.updateStorageFilter(filterKey, {filter})
    }, [filterKey, filter])

    const onSearchInput = useCallback(
        (search = "") => {
            setSearch(search)
            model.updateStorageFilter(filterKey, {search})
        },
        [filterKey]
    )

    //#endregion filter

    const paymentPlanOptions = useMemo(() => {
        const seen = new Map<number, {id: number; name: string}>()
        for (const payment of allPayments) {
            for (const planItem of payment.paymentPlanItems ?? []) {
                if (planItem.paymentPlan && !seen.has(planItem.paymentPlan.paymentPlanId)) {
                    seen.set(planItem.paymentPlan.paymentPlanId, {
                        id: planItem.paymentPlan.paymentPlanId,
                        name: `${planItem.paymentPlan.code} - ${planItem.paymentPlan.title}`
                    })
                }
            }
        }
        return Array.from(seen.values())
    }, [allPayments])

    const getData = React.useCallback(async () => {
        try {
            dispatch({isLoading: true})
            const raw = await studentPaymentService.getStudentRecurringPayments(profileId)
            setAllPayments(raw)
        } catch (error) {
            console.error(error)
        } finally {
            dispatch({isLoading: false})
        }
    }, [profileId])

    useEffect(() => {
        const selectedPlanIds: number[] = appliedFilter.paymentPlans?.map((p: any) => p.id) ?? []
        const filtered = selectedPlanIds.length
            ? allPayments.filter((rp) =>
                  rp.paymentPlanItems?.some((item) => selectedPlanIds.includes(item.paymentPlanId))
              )
            : allPayments
        dispatch({
            total: filtered.length,
            data: filtered.map((item) => ({...item, id: item.recurringPaymentId}))
        })
    }, [allPayments, appliedFilter])

    const pauseRecurringPayment = React.useCallback(
        async (item: StudentRecurringPayment) => {
            try {
                dispatch({isLoading: true})
                await studentPaymentService.pauseStudentRecurringPayment(item.recurringPaymentId)
                getData()
            } catch (error) {
                handleError(error)
            } finally {
                dispatch({isLoading: false})
            }
        },
        [profileId, getData]
    )

    const unpauseRecurringPayment = React.useCallback(
        async (item: StudentRecurringPayment) => {
            try {
                dispatch({isLoading: true})
                await studentPaymentService.unpauseStudentRecurringPayment(item.recurringPaymentId)
                getData()
            } catch (error) {
                handleError(error)
            } finally {
                dispatch({isLoading: false})
            }
        },
        [profileId, getData]
    )

    const cancelRecurringPayment = React.useCallback(
        async (item: StudentRecurringPayment) => {
            try {
                dispatch({isLoading: true})
                await studentPaymentService.cancelStudentRecurringPayment(item.recurringPaymentId)
                getData()
            } catch (error) {
                handleError(error)
            } finally {
                dispatch({isLoading: false})
            }
        },
        [profileId, getData]
    )

    const resumeRecurringPayment = React.useCallback(
        async (item: StudentRecurringPayment) => {
            try {
                dispatch({isLoading: true})
                await studentPaymentService.resumeStudentRecurringPayment(item.recurringPaymentId)
                getData()
            } catch (error) {
                handleError(error)
            } finally {
                dispatch({isLoading: false})
            }
        },
        [profileId, getData]
    )

    const deleteRecurringPayment = React.useCallback(
        async (item: StudentRecurringPayment) => {
            try {
                dispatch({isLoading: true})
                await studentPaymentService.deleteStudentRecurringPayment(item.recurringPaymentId)
                getData()
            } catch (error) {
                handleError(error)
            } finally {
                dispatch({isLoading: false})
            }
        },
        [profileId, getData]
    )

    const syncRecurringPayment = React.useCallback(
        async (item: StudentRecurringPayment) => {
            try {
                dispatch({isLoading: true})
                await studentPaymentService.syncStudentRecurringPayment(item.recurringPaymentId)
                getData()
            } catch (error) {
                handleError(error)
            } finally {
                dispatch({isLoading: false})
            }
        },
        [profileId, getData]
    )

    const onClickRowItem = React.useCallback((item) => {}, [])

    const onClickEdit = React.useCallback((item) => {}, [])

    const onClickDelete = React.useCallback((item) => {}, [])

    const getFields = React.useCallback(
        () => [
            t("column.id"),
            t("column.paymentPlan", "Payment Plan"),
            t("column.status"),
            t("column.amount"),
            t("column.interval"),
            t("column.lastBilledDate"),
            t("column.nextBillingDate"),
            t("column.cancelAt"),
            ""
        ],
        [t]
    )

    const [payPendingTarget, setPayPendingTarget] = useState<StudentRecurringPayment | null>(null)
    const recurringPaymentPopup = useVisible(false)

    const getColumns = React.useCallback(
        () => [
            {
                title: t("column.id"),
                field: "recurringPaymentId",
                columnIndex: 1
            },
            {
                title: t("column.paymentPlan", "Payment Plan"),
                field: "paymentPlanItems",
                columnIndex: 2,
                render: (value: StudentRecurringPaymentPlanItem[], record: any) => {
                    if (!value?.length) return null
                    const plans = uniqBy(
                        value.filter((item) => item.paymentPlan),
                        (item) => item.paymentPlanId
                    )
                    if (!plans.length) return null
                    // record is passed as an argument (always fresh from HOC data state),
                    // so record.isExpanded never suffers from stale-closure issues.
                    const isExpanded = !!record.isExpanded
                    return (
                        <div>
                            <div style={{display: "flex", alignItems: "center", gap: 8}}>
                                <span>
                                    {plans
                                        .map((item) => `${item.paymentPlan!.code} - ${item.paymentPlan!.title}`)
                                        .join(", ")}
                                </span>
                                <span
                                    style={{cursor: "pointer", fontSize: 12, color: "#1890ff", whiteSpace: "nowrap"}}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onUpdateRowData({...record, isExpanded: !isExpanded}, record)
                                    }}>
                                    {isExpanded ? "▲ Hide" : "▼ Items"}
                                </span>
                            </div>
                            {isExpanded && (
                                <table
                                    style={{
                                        marginTop: 8,
                                        fontSize: 12,
                                        width: "100%",
                                        borderCollapse: "collapse"
                                    }}>
                                    <thead>
                                        <tr style={{background: "#f5f5f5"}}>
                                            <th style={{padding: "4px 8px", textAlign: "left", fontWeight: 600}}>
                                                Due Date
                                            </th>
                                            <th style={{padding: "4px 8px", textAlign: "right", fontWeight: 600}}>
                                                Amount
                                            </th>
                                            <th style={{padding: "4px 8px", textAlign: "right", fontWeight: 600}}>
                                                Paid
                                            </th>
                                            <th style={{padding: "4px 8px", textAlign: "left", fontWeight: 600}}>
                                                Status
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {value.map((item) => (
                                            <tr
                                                key={item.paymentPlanItemId}
                                                style={{borderBottom: "1px solid #f0f0f0"}}>
                                                <td style={{padding: "4px 8px"}}>
                                                    {item.dueDate ? moment(item.dueDate).format(userDateFormat) : "—"}
                                                </td>
                                                <td style={{padding: "4px 8px", textAlign: "right"}}>
                                                    {formatCurrency(item.amount)}
                                                </td>
                                                <td style={{padding: "4px 8px", textAlign: "right"}}>
                                                    {formatCurrency(item.paidAmount ?? 0)}
                                                </td>
                                                <td style={{padding: "4px 8px"}}>
                                                    {item.itemStatus === PaymentPlanItemStatus.Paid ? (
                                                        <span style={{color: "#52c41a", fontWeight: 600}}>Paid</span>
                                                    ) : item.itemStatus === PaymentPlanItemStatus.Failed ? (
                                                        <span style={{color: "#ff4d4f", fontWeight: 600}}>Failed</span>
                                                    ) : item.itemStatus === PaymentPlanItemStatus.PastDue ? (
                                                        <span style={{color: "#faad14", fontWeight: 600}}>
                                                            Past Due
                                                        </span>
                                                    ) : item.itemStatus === PaymentPlanItemStatus.Canceled ? (
                                                        <span style={{color: "#8c8c8c", fontWeight: 600}}>
                                                            Canceled
                                                        </span>
                                                    ) : (
                                                        <span style={{color: "#595959"}}>Active</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )
                }
            },
            {
                title: t("column.status"),
                field: "status",
                columnIndex: 3
            },
            {
                title: t("column.amount"),
                field: "amountInCents",
                columnIndex: 4,
                render: (value, record: StudentRecurringPayment) => formatCurrency(value / 100, record.currency)
            },
            {
                title: t("column.interval"),
                field: "interval",
                columnIndex: 5
            },
            {
                title: t("column.lastBilledDate"),
                field: "lastBilledAt",
                columnIndex: 6,
                render: (value) => value && moment(value).format(userDateFormat)
            },
            {
                title: t("column.nextBillingDate"),
                field: "nextBillingAt",
                columnIndex: 7,
                render: (value, record: StudentRecurringPayment) => {
                    return value && moment(value).format(userDateFormat)
                }
            },
            {
                title: t("column.cancelAt"),
                field: "cancelAt",
                columnIndex: 8,
                render: (value) => value && moment(value).format(userDateFormat)
            },
            {
                title: "",
                field: "recurringPaymentId",
                columnIndex: 9,
                classNameBody: styles.payActionCell,
                render: (_value, record: StudentRecurringPayment) =>
                    (() => {
                        const rawMeta = record.metadata
                        const parsedMeta: Record<string, any> =
                            typeof rawMeta === "string"
                                ? (() => {
                                      try {
                                          return JSON.parse(rawMeta) ?? {}
                                      } catch {
                                          return {}
                                      }
                                  })()
                                : rawMeta ?? {}
                        const approvalUrl = parsedMeta.pendingRevisionApprovalUrl as string | undefined
                        if (approvalUrl) {
                            return (
                                <BaseButton
                                    size="small"
                                    variant="secondary"
                                    uppercase={false}
                                    bold={false}
                                    className={styles.payButton}
                                    title={t("label.approveInPaypal", "Approve in PayPal")}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        window.open(approvalUrl, "_blank", "noopener,noreferrer")
                                    }}
                                />
                            )
                        }
                        if (record.status === RecurringPaymentStatus.Pending) {
                            return (
                                <BaseButton
                                    size="small"
                                    variant="secondary"
                                    uppercase={false}
                                    bold={false}
                                    className={styles.payButton}
                                    title={t("label.pay")}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        recurringPaymentPopup.close()
                                        setPayPendingTarget(record)
                                    }}
                                />
                            )
                        }
                        return null
                    })()
            }
        ],
        [t, userDateFormat, recurringPaymentPopup, onUpdateRowData]
    )

    const getTableHeaderActions = React.useCallback((isShowDuplicateBtn = true, checkedData = []) => [], [])

    const [changePaymentMethodTarget, setChangePaymentMethodTarget] = useState<StudentRecurringPayment | null>(null)
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<number | null>(null)

    const onOpenChangePaymentMethod = useCallback((item: StudentRecurringPayment) => {
        setChangePaymentMethodTarget(item)
        setSelectedPaymentMethodId(item.paymentMethodId ?? null)
    }, [])

    const getMenuActions = React.useCallback(() => {
        return [
            {
                title: t("label.changePaymentMethod", "Change Payment Method"),
                icon: "CREDIT_CARD",
                action: onOpenChangePaymentMethod,
                canShow: (item: StudentRecurringPayment) => item.provider === PaymentProvider.Stripe
            },
            {
                title: t("label.pause", "Pause"),
                icon: "PAUSE",
                action: pauseRecurringPayment,
                canShow: (item: StudentRecurringPayment) =>
                    item.status === RecurringPaymentStatus.Active &&
                    (item.provider === PaymentProvider.Stripe ||
                        item.provider === PaymentProvider.Paypal ||
                        item.provider === PaymentProvider.DirectEmbed)
            },
            {
                title: t("label.unpause", "Unpause"),
                icon: "RESUME",
                action: unpauseRecurringPayment,
                canShow: (item: StudentRecurringPayment) =>
                    item.status === RecurringPaymentStatus.Paused &&
                    (item.provider === PaymentProvider.Stripe ||
                        item.provider === PaymentProvider.Paypal ||
                        item.provider === PaymentProvider.DirectEmbed)
            },
            {
                title: t("label.cancel"),
                icon: "EDIT",
                action: cancelRecurringPayment,
                canShow: (item: StudentRecurringPayment) => !item.cancelAt
            },
            {
                title: t("label.resume"),
                icon: "RESUME",
                action: resumeRecurringPayment,
                canShow: (item: StudentRecurringPayment) => item.status === RecurringPaymentStatus.Canceled
            },
            {
                title: t("label.sync", "Sync"),
                icon: "REFRESH",
                action: syncRecurringPayment,
                canShow: () => true
            },
            {
                title: t("label.delete"),
                icon: "DELETE",
                action: deleteRecurringPayment,
                canShow: (item: StudentRecurringPayment) => true
            }
        ]
    }, [
        t,
        onOpenChangePaymentMethod,
        pauseRecurringPayment,
        unpauseRecurringPayment,
        cancelRecurringPayment,
        resumeRecurringPayment,
        syncRecurringPayment,
        deleteRecurringPayment
    ])

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: () => ""},
            {key: "getFilterMemoryKey", func: () => filterKey},
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "getTableHeaderActions", func: getTableHeaderActions},
            {key: "getMenuActions", func: getMenuActions},
            {key: "onClickEdit", func: onClickEdit},
            {key: "onClickRowItem", func: onClickRowItem}
        ])
    }, [])

    useEffect(() => {
        if (isLoadedTableFuncs) {
            getData()
        }
    }, [isLoadedTableFuncs, search, profileId])

    const handleAddedRecurringPaymentSuccess = useCallback(() => {
        recurringPaymentPopup.close()
        setPayPendingTarget(null)
        getData()
    }, [getData, recurringPaymentPopup])

    const handleCloseRecurringPaymentPopup = useCallback(() => {
        recurringPaymentPopup.close()
        setPayPendingTarget(null)
    }, [recurringPaymentPopup])

    const [isChangingPaymentMethod, setIsChangingPaymentMethod] = useState(false)

    const {paymentMethods} = useStudentPaymentMethods({profileId})
    const stripePaymentMethods = useMemo(
        () => paymentMethods.filter((m: StudentPaymentMethod) => m.provider === PaymentProvider.Stripe),
        [paymentMethods]
    )
    const paymentMethodOptions = useMemo(
        () =>
            stripePaymentMethods.map((m: StudentPaymentMethod) => ({
                id: m.paymentMethodId,
                name: m.last4
                    ? `${(m.cardBrand ?? "").toUpperCase()} **** ${m.last4} (${m.expMonth}/${m.expYear})`
                    : m.bankName ?? String(m.paymentMethodId)
            })),
        [stripePaymentMethods]
    )

    const onConfirmChangePaymentMethod = useCallback(async () => {
        if (!changePaymentMethodTarget || !selectedPaymentMethodId) return
        try {
            setIsChangingPaymentMethod(true)
            await studentPaymentService.changeStudentRecurringPaymentMethod(
                changePaymentMethodTarget.recurringPaymentId,
                selectedPaymentMethodId
            )
            toastSuccess(t("msg.recurringPayment.paymentMethodChanged", "Payment method updated successfully"))
            setChangePaymentMethodTarget(null)
            setSelectedPaymentMethodId(null)
            getData()
        } catch (error) {
            handleError(error)
        } finally {
            setIsChangingPaymentMethod(false)
        }
    }, [changePaymentMethodTarget, selectedPaymentMethodId, getData, t])

    return (
        <div className={styles.wrap}>
            <BaseNewFilter
                className="mb-24"
                filter={filter}
                onClick={onApplyFilter}
                onClickClear={onClearFilter}
                renderRightFilter={() => (
                    <div className="flex justify-end items-center">
                        <BaseButton
                            title={t("common:action.add")}
                            className="ml-24"
                            onClick={() => {
                                setPayPendingTarget(null)
                                recurringPaymentPopup.open()
                            }}
                        />
                    </div>
                )}
                onSearchInput={onSearchInput}>
                <Row gutter={[24, 24]}>
                    <Col span={12}>
                        <KlassDropdown
                            isMulti
                            isClearable
                            placeholder={t("column.paymentPlan", "Payment Plan")}
                            options={paymentPlanOptions}
                            value={filter.paymentPlans}
                            onChange={(options: {id: number; name: string}[]) =>
                                onChangeFilter("paymentPlans", options || [])
                            }
                        />
                    </Col>
                </Row>
            </BaseNewFilter>

            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={onChangePage}
                onChangeRowPerPage={onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={onChangeFields}
                onChangeAllFields={onChangeAllFields}
                onDraggableColumn={onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                menuActions={isHideMenuActions ? [] : menuActions}
                fields={fields}
                allFields={allFields}
                isShowCheckedColumn={false}
                onClickRowItem={onClickRowItem}
                onChangeFields={onChangeFields}
                onUpdateRowData={onUpdateRowData}
                onUpdateTableData={onUpdateTableData}
                onDraggableColumn={onDraggableColumn}
                onChangeAllFields={onChangeAllFields}
            />

            <RecurringPaymentPopup
                isShow={recurringPaymentPopup.isVisible || !!payPendingTarget}
                onClose={handleCloseRecurringPaymentPopup}
                onSuccess={handleAddedRecurringPaymentSuccess}
                profileId={profileId}
                pendingRecurringPayment={payPendingTarget}
            />

            <Modal
                title={t("label.changePaymentMethod", "Change Payment Method")}
                open={!!changePaymentMethodTarget}
                onCancel={() => {
                    setChangePaymentMethodTarget(null)
                    setSelectedPaymentMethodId(null)
                }}
                onOk={onConfirmChangePaymentMethod}
                okText={t("common:action.save", "Save")}
                confirmLoading={isChangingPaymentMethod}
                destroyOnClose>
                <div className="mt-24 mb-24">
                    <KlassDropdown
                        isClearable={false}
                        options={paymentMethodOptions}
                        value={paymentMethodOptions.find((o) => o.id === selectedPaymentMethodId) ?? null}
                        onChange={(option: {id: number; name: string} | null) =>
                            setSelectedPaymentMethodId(option?.id ?? null)
                        }
                    />
                </div>
            </Modal>
        </div>
    )
}

export default KlassappTableHOC(RecurringPayments)
